import React, { useState, useRef, useEffect } from "react";
import ButtonMenu from "../../header/elements/ButtonMenu";
import DynamicNav from "../../nav/DynamicNav";
import { useNavigate } from "react-router-dom";
import LogoHeader from "../../header/elements/LogoHeader";
import LogoIcon from "../../../icons/LogoIcon";
import useFileManagerApi from "../../../hooks/api/useFileManagerApi";

const HeaderDashboard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [quota, setQuota] = useState({
    used: 0,
    max: 0,
    available: 0,
    percentageUsed: 0
  });
  const [loading, setLoading] = useState(true);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { getQuota } = useFileManagerApi();

  const navLinks = [
    { src: `${process.env.PUBLIC_URL}/envelope.svg`, url: "/dashboard", text: "My messages" },
    { src: `${process.env.PUBLIC_URL}/folderDark.svg`, url: "/dashboard/my-files", text: "My files" },
    { src: `${process.env.PUBLIC_URL}/settings.svg`, url: "/dashboard/settings", text: "Settings" },
    { src: `${process.env.PUBLIC_URL}/doc.svg`, url: "/dashboard/billing", text: "Billing info" },
  ];

  const handleLogout = () => {
    document.cookie =
      "AccessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/login");
  };

  const handleToggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  const handleCloseMenu = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchQuota = async () => {
      try {
        setLoading(true);
        const response = await getQuota();
        if (response.data) {
          setQuota({
            used: response.data.used || 0,
            max: response.data.max || 0,
            available: response.data.available || 0,
            percentageUsed: response.data.percentageUsed || 0
          });
        }
      } catch (error) {
        console.error("Error fetching quota:", error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    fetchQuota();
  }, [getQuota]);

  const formatBytesToGB = (bytes) => {
    if (!bytes || bytes === 0) return '0';
    const gb = bytes / (1024 * 1024 * 1024);
    return Math.round(gb * 100) / 100;
  };

  return (
    <div className="header-menu relative" ref={menuRef}>
      {isOpen && (
        <div
          className="fixed inset-0 z-10 bg-black/30 desktop:hidden transition-opacity duration-300 ease-in-out"
          onClick={handleCloseMenu}
          aria-hidden="true"
        />
      )}
      <div className="p-6 mobile:p-4 border-b-2 desktop:hidden relative z-30 bg-white">
        <div className="relative flex items-center">
          <ButtonMenu toggleMenu={handleToggleMenu} isOpen={isOpen} />
          <div className="absolute left-1/2 -translate-x-1/2">
            <LogoHeader />
          </div>
        </div>
      </div>

      <div
        className={`
          flex flex-col h-full bg-white shadow-shadow5 border mobile:pl-3 pl-6 py-6 pr-3
          mobile:fixed mobile:top-0 mobile:left-0 mobile:h-screen mobile:w-4/5 mobile:max-w-[320px]
          desktop:w-[300px] z-30
          mobile:transform mobile:transition-transform mobile:duration-300 mobile:ease-in-out
          ${isOpen ? "mobile:translate-x-0 mobile:pointer-events-auto" : "mobile:-translate-x-full mobile:pointer-events-none"}
        `}
      >
        <div>
          <div className="flex items-center justify-between">
            <LogoIcon />
            <ButtonMenu
              hidden="desktop:hidden"
              toggleMenu={handleToggleMenu}
              isOpen={isOpen}
            />
          </div>

          <div className="menu flex flex-col pt-8 pb-64 space-y-2">
            <DynamicNav
              links={navLinks}
              hover="hover:bg-gray2 hover:rounded-lg"
              navigateTo={true}
              onLinkClick={handleCloseMenu}
            />
            <div className="basis-1/4 pt-2 flex ml-4 cursor-pointer desktop:hidden">
              <img src={`${process.env.PUBLIC_URL}/mini.svg`} alt="mini" />

              <button
                className="text-primary text-bodyMM pl-3"
                onClick={handleLogout}
              >
                Log out
              </button>
            </div>
          </div>
        </div>

        <div className="storage flex flex-col items-start space-y-2 mx-6 mt-auto">
          <p className="mr-2 text-bodyMR text-black">Storage</p>
          {loading ? (
            <p className="text-bodyESR text-gray5">Loading...</p>
          ) : (
            <>
              <div className="relative w-full h-2 bg-gray-200 rounded-full">
                <div
                  className="absolute top-0 left-0 h-2 bg-[#FF5400] rounded-full transition-all duration-300"
                  style={{ width: `${quota.percentageUsed}%` }}
                ></div>
              </div>
              <p className="text-bodyESR text-gray5">
                <span className="text-primary text-bodyESM">{formatBytesToGB(quota.used)} GB</span> used of {formatBytesToGB(quota.max)}
                GB ({quota.percentageUsed}%)
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderDashboard;
